import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import SiteLayout from "@/components/feature/SiteLayout";
import MovieGrid from "@/components/feature/MovieGrid";
import Pagination from "@/components/feature/Pagination";
import FilterGroup from "@/pages/category/components/FilterGroup";
import {
  applyFilters,
  getGenreInfo,
  getMoviesByGenre,
  getRegionsInGenre,
  getSubGenres,
  getYearsInGenre,
  paginate,
} from "@/utils/movie";

const PAGE_SIZE = 6;
type SortKey = "time" | "rating" | "heat";

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "time", label: "更新时间" },
  { key: "rating", label: "评分" },
  { key: "heat", label: "热度" },
];

export default function CategoryPage() {
  const { genre = "" } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get("page")) || 1;

  const [subGenre, setSubGenre] = useState("");
  const [region, setRegion] = useState("");
  const [year, setYear] = useState("");
  const [sortBy, setSortBy] = useState<SortKey>("time");
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  useEffect(() => {
    setSubGenre("");
    setRegion("");
    setYear("");
    setSortBy("time");
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genre]);

  const genreInfo = getGenreInfo(genre);
  const baseList = useMemo(() => getMoviesByGenre(genre), [genre]);

  const subGenres = useMemo(() => getSubGenres(genre), [genre]);
  const regionOptions = useMemo(() => getRegionsInGenre(genre), [genre]);
  const yearOptions = useMemo(() => getYearsInGenre(genre), [genre]);

  const filtered = useMemo(
    () =>
      applyFilters(baseList, {
        subGenre: subGenre || undefined,
        region: region || undefined,
        year: year ? Number(year) : "",
        sortBy,
      }),
    [baseList, subGenre, region, year, sortBy]
  );

  const { items, total, totalPages, current } = paginate(
    filtered,
    page,
    PAGE_SIZE
  );

  const updatePage = (next: number) => {
    if (next < 1 || next > totalPages) return;
    setSearchParams(next === 1 ? {} : { page: String(next) });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetFilters = () => {
    setSubGenre("");
    setRegion("");
    setYear("");
    setSortBy("time");
    setSearchParams({}, { replace: true });
  };

  const hasFilter = Boolean(subGenre || region || year) || sortBy !== "time";

  const typeOptions = [
    { label: "全部", value: "" },
    ...subGenres.map((g) => ({ label: g, value: g })),
  ];
  const regionChips = [
    { label: "全部", value: "" },
    ...regionOptions.map((r) => ({ label: r, value: r })),
  ];
  const yearChips = [
    { label: "全部", value: "" },
    ...yearOptions.map((y) => ({ label: String(y), value: String(y) })),
  ];

  const filtersBlock = (
    <div className="flex flex-col">
      <FilterGroup
        label="类型"
        options={typeOptions}
        selected={subGenre}
        onSelect={(v) => {
          setSubGenre(v);
          setSearchParams({}, { replace: true });
        }}
      />
      <FilterGroup
        label="地区"
        options={regionChips}
        selected={region}
        onSelect={(v) => {
          setRegion(v);
          setSearchParams({}, { replace: true });
        }}
      />
      <FilterGroup
        label="年份"
        options={yearChips}
        selected={year}
        onSelect={(v) => {
          setYear(v);
          setSearchParams({}, { replace: true });
        }}
      />
    </div>
  );

  const title = `${genreInfo?.name || genre}电影免费观看 - 免费电影在线观看`;
  const description =
    genreInfo?.description ||
    `提供丰富${genre}电影资源，免费在线观看最新${genre}大片`;
  const keywords = `${genre}电影,${genre}电影免费观看,${genre}电影在线观看,免费电影在线观看,高清电影`;

  return (
    <SiteLayout>
      <Seo title={title} description={description} keywords={keywords} />

      <div className="border-b border-background-200 bg-primary-50">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
          <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-600 cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">
              {genreInfo?.name || genre}电影
            </span>
          </nav>
          <div className="mt-3 flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500 text-2xl text-background-50">
              <i className={genreInfo?.icon || "ri-film-line"} />
            </span>
            <div>
              <h1 className="font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
                {genreInfo?.name || genre}电影
              </h1>
              <p className="mt-1 text-sm text-foreground-600">{description}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6">
        <div className="hidden rounded-lg border border-background-200 bg-background-100 px-5 py-2 lg:block">
          {filtersBlock}
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setPanelOpen(true)}
              className="flex items-center gap-1.5 rounded-md border border-background-300 bg-background-100 px-3.5 py-2 text-sm font-medium text-foreground-800 hover:border-primary-300 cursor-pointer lg:hidden whitespace-nowrap"
            >
              <i className="ri-filter-3-line" />
              筛选
              {hasFilter && (
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-accent-500 text-[10px] text-primary-950">
                  !
                </span>
              )}
            </button>
            <p className="text-sm text-foreground-600">
              共 <span className="font-semibold text-primary-600">{total}</span> 部影片
              {totalPages > 1 && (
                <span className="ml-2 text-foreground-400">
                  第 {current} / {totalPages} 页
                </span>
              )}
            </p>
          </div>

          <div className="flex items-center gap-1 rounded-md border border-background-300 bg-background-100 p-1">
            {sortOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => setSortBy(option.key)}
                className={`flex items-center gap-1 rounded-md px-3 py-1.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  sortBy === option.key
                    ? "bg-secondary-500 text-background-50"
                    : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6">
          {items.length > 0 ? (
            <MovieGrid movies={items} />
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-500">
                <i className="ri-emotion-sad-line" />
              </span>
              <p className="mt-4 text-sm text-foreground-600">
                没有找到符合条件的{genreInfo?.name || genre}电影
              </p>
              <p className="mt-1 text-xs text-foreground-500">
                试着减少筛选条件，或重置后再看看
              </p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-5 flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-refresh-line" />
                重置筛选
              </button>
            </div>
          )}
        </div>

        <Pagination current={current} totalPages={totalPages} onChange={updatePage} />
      </div>

      {panelOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setPanelOpen(false)}
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-background-50 p-4 animate-fade-up">
            <div className="flex items-center justify-between pb-3">
              <h2 className="font-heading text-base font-bold text-foreground-950">
                筛选条件
              </h2>
              <button
                type="button"
                onClick={() => setPanelOpen(false)}
                aria-label="关闭筛选"
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700 cursor-pointer"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            {filtersBlock}
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={resetFilters}
                className="flex-1 rounded-md border border-background-300 py-2.5 text-sm font-medium text-foreground-700 cursor-pointer whitespace-nowrap"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setPanelOpen(false)}
                className="flex-1 rounded-md bg-primary-500 py-2.5 text-sm font-semibold text-background-50 cursor-pointer whitespace-nowrap"
              >
                查看结果
              </button>
            </div>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}